import { ITableService } from 'src/app/common/interfaces/table/table-service.interface';
import { IWebMessage } from 'src/app/common/interfaces/webmessage-interface';
import { IDecisionedNotificationCount } from '../../interfaces/transaction-dual-approval/decisioned-notification-count.interface';
import { IDecisionedNotificationCountByTransactionType } from '../../interfaces/transaction-dual-approval/decisioned-notification-count-by-transaction-type.interface';
import { IDualApprovalDecision } from '../../interfaces/transaction-dual-approval/dual-approval-decision-request.interface';
import {
  IDualApprovalRequiredRequest,
  IDualApprovalRequiredResponse,
} from '../../interfaces/transaction-dual-approval/dual-approval-required.interface';
import { IFedNowTransactionDualApprovalSummary } from '../../interfaces/transaction-dual-approval/dual-approval-summary.interface';
import { IPendingDualApprovalCount } from '../../interfaces/transaction-dual-approval/pending-dual-approval-count.interface';
import { IUnfinishedDualApprovalCount } from '../../interfaces/transaction-dual-approval/unfinished-dual-approval-count.interface';

/**
 * Abstract service for FedNow Transaction Dual Approval operations.
 * Implementations will provide logic to interact with the backend API or
 * mock logic for local development.
 */
export abstract class AbstractTransactionDualApprovalService extends ITableService {
  /**
   * Returns the count of FedNow transactions that have not yet completed
   * the dual approval process (i.e. still awaiting approval or rejection).
   * This includes pending approvals as well as those that are in the process
   * of being reviewed.
   */
  abstract getUnfinishedFedNowTransactionApprovalCount(options?: {
    skipLoading?: boolean;
  }): Promise<IUnfinishedDualApprovalCount>;

  /**
   * Returns the count of RTP transactions that have not yet completed
   * the dual approval process.
   */
  abstract getUnfinishedRtpTransactionApprovalCount(options?: {
    skipLoading?: boolean;
  }): Promise<IUnfinishedDualApprovalCount>;

  /**
   * Returns the count of FedNow transactions that are currently pending
   * the dual approval process.
   */
  abstract getPendingFedNowTransactionApprovalCount(options?: {
    skipLoading?: boolean;
  }): Promise<IPendingDualApprovalCount>;

  /**
   * Returns decisioned notification counts grouped by approval status
   * for the current user.
   */
  abstract getDecisionedNotificationCountsByStatus(options?: {
    skipLoading?: boolean;
    transactionType?: string;
  }): Promise<IDecisionedNotificationCount[]>;

  /**
   * Returns decisioned notification counts grouped by transaction type and approval status
   * for the current user.
   */
  abstract getDecisionedNotificationCountsByTransactionType(options?: {
    skipLoading?: boolean;
  }): Promise<IDecisionedNotificationCountByTransactionType[]>;

  /**
   * Clears all pending decisioned notifications for the current user.
   */
  abstract clearDecisionedNotifications(options?: {
    skipLoading?: boolean;
    transactionType?: string;
  }): Promise<IWebMessage>;

  /**
   * Retrieves the full details of a single FedNow Transaction Dual Approval summary entry.
   * @param id - The ID of the dual approval summary entry.
   */
  abstract getSummary(id: number): Promise<IFedNowTransactionDualApprovalSummary>;

  /**
   * Approves a FedNow Transaction Dual Approval summary entry.
   * @param request - The decision request containing the ID, action, and optional comment.
   */
  abstract approve(request: IDualApprovalDecision): Promise<IWebMessage>;

  /**
   * Denies a FedNow Transaction Dual Approval summary entry.
   * @param request - The decision request containing the ID, action, and required comment.
   */
  abstract deny(request: IDualApprovalDecision): Promise<IWebMessage>;

  /**
   * Approves a FedNow RFP Decision dual approval summary entry.
   * @param request - The decision request containing the ID and optional comment.
   */
  abstract rfpApprove(request: IDualApprovalDecision): Promise<IWebMessage>;

  /**
   * Denies a FedNow RFP Decision dual approval summary entry.
   * @param request - The decision request containing the ID, required comment, and reason code.
   */
  abstract rfpDeny(request: IDualApprovalDecision): Promise<IWebMessage>;

  /**
   * Marks a FedNow Transaction Dual Approval summary entry as closed without
   * an approve/deny decision from the current reviewer.
   * @param dualSummaryId - The dual approval summary identifier.
   */
  abstract noDecision(dualSummaryId: number): Promise<IWebMessage>;

  /**
   * Determines whether dual approval is required for a FedNow transaction
   * with the specified details.
   * @param request - The request containing the transaction amount.
   */
  abstract isDualApprovalRequired(
    request: IDualApprovalRequiredRequest
  ): Promise<IDualApprovalRequiredResponse>;
}
